import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  FacilityGroupEvent,
  FacilityGroupEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/core/v1/facilityGroup_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { FacilityGroupCreatedDetail } from './FacilityGroupCreatedDetail';
import { FacilityGroupDeletedDetail } from './FacilityGroupDeletedDetail';
import { FacilityGroupUpdatedDetail } from './FacilityGroupUpdatedDetail';

import { useTranslations } from '@/facilities/i18n/useTranslations';

type FacilityGroupEventDetailProps = {
  payload: string;
};

export const FacilityGroupEventDetail = ({
  payload,
}: FacilityGroupEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): FacilityGroupEvent | null => {
    try {
      return fromJson(FacilityGroupEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title={t.logs.rawPayloadParseError} />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <FacilityGroupCreatedDetail event={inner.value} />;
      case 'updated':
        return <FacilityGroupUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <FacilityGroupDeletedDetail event={inner.value} />;
      case undefined:
        return <JsonViewer data={payload} title={t.logs.unknownEventType} />;
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return t.logs.events.facilityGroupCreated;
      case 'updated':
        return t.logs.events.facilityGroupUpdated;
      case 'deleted':
        return t.logs.events.facilityGroupDeleted;
      case undefined:
        return t.logs.unknownEvent;
    }
  };

  return (
    <Col className="@FacilityGroupEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
